import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import toast from 'react-hot-toast';
import { RotateCcw, Trash2, AlertTriangle, Lock, ChevronDown, ChevronUp, Calendar } from 'lucide-react';

const BackupReports = () => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, type: '', report: null, password: '', adminUsername: '', projectNameConfirm: '' });
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedReportId, setSelectedReportId] = useState(null);
  const [filterProjectName, setFilterProjectName] = useState('');
  const [filterDate, setFilterDate] = useState('');
  const [isProjectDropdownOpen, setIsProjectDropdownOpen] = useState(false);
  const [isDateDropdownOpen, setIsDateDropdownOpen] = useState(false);

  const uniqueDates = [...new Set(reports.map(r => r.report_date || 'N/A'))].sort((a, b) => b.localeCompare(a));
  const uniqueProjects = [...new Set(reports.map(r => r.report_name))].sort();

  const filteredReports = reports.filter(r => {
    const matchDate = filterDate ? (r.report_date || 'N/A') === filterDate : true;
    const matchProject = filterProjectName ? r.report_name.toLowerCase().includes(filterProjectName.toLowerCase()) : true;
    return matchDate && matchProject;
  });

  const groupedReports = filteredReports.reduce((acc, r) => {
    const date = r.report_date || 'N/A';
    if (!acc[date]) acc[date] = [];
    acc[date].push(r);
    return acc;
  }, {});

  useEffect(() => {
    fetchBackupReports();
  }, []);

  const fetchBackupReports = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/backup/hospital-reports`, { withCredentials: true });
      setReports(res.data);
    } catch (err) {
      toast.error('Failed to fetch archival reports');
    } finally {
      setLoading(false);
    }
  };

  const handleRestore = async (id) => {
    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/api/backup/hospital-reports/${id}/restore`, {}, { withCredentials: true });
      toast.success('Report restored successfully');
      fetchBackupReports();
      setFilterProjectName('');
      setFilterDate('');
    } catch (err) {
      toast.error('Failed to restore report');
    }
  };

  const handlePermanentDelete = async (e) => {
    e.preventDefault();
    const { report, password, adminUsername, projectNameConfirm } = confirmModal;

    if (projectNameConfirm !== report.report_name) {
      toast.error('Project name does not match');
      return;
    }

    if (!adminUsername || !password) {
      toast.error('Admin username and password are required');
      return;
    }

    try {
      await axios.post(
        `${process.env.REACT_APP_API_URL}/api/backup/hospital-reports/${report.id}/permanent`,
        { password, user_name: adminUsername },
        { withCredentials: true }
      );
      toast.success('Report permanently deleted');
      fetchBackupReports();
      setFilterProjectName('');
      setFilterDate('');
      closeModal();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to delete report');
    }
  };

  const closeModal = () => {
    setConfirmModal({ isOpen: false, type: '', report: null, password: '', adminUsername: '', projectNameConfirm: '' });
  };

  return (
    <Layout title="Archival Reports" subtitle="Restore or permanently delete projects">
      <div className="bg-[#0B1120]/80 backdrop-blur-2xl border border-white/[0.05] rounded-3xl p-6 md:p-8 min-h-[70vh] shadow-[0_8px_32px_rgba(0,0,0,0.5)]">

        {/* Header */}
        <div className="flex items-center justify-between mb-8 pb-6 border-b border-white/[0.05]">
          <div>
            <h2 className="text-2xl font-bold bg-gradient-to-r from-emerald-300 to-indigo-300 bg-clip-text text-transparent">Deleted Projects</h2>
            <p className="text-sm text-slate-400 mt-1">Manage projects that have been moved to archival</p>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-5 mb-8 bg-slate-900/40 p-6 rounded-3xl border border-slate-700/40 shadow-inner">
          <div className="flex-1 relative">
             <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2 block">Filter by Project Name</label>
             <div className="relative">
               <input 
                 type="text" 
                 placeholder="Type or select project name..." 
                 value={filterProjectName} 
                 onChange={e => {
                   setFilterProjectName(e.target.value);
                   setIsProjectDropdownOpen(true);
                 }}
                 onFocus={() => setIsProjectDropdownOpen(true)}
                 onBlur={() => setTimeout(() => setIsProjectDropdownOpen(false), 200)}
                 className="w-full bg-black/40 border border-slate-700 text-white placeholder-slate-500 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-indigo-500/50 transition-all shadow-inner pr-10"
               />
               <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none">
                 <ChevronDown className={`w-4 h-4 text-slate-500 transition-transform duration-300 ${isProjectDropdownOpen ? 'rotate-180' : ''}`} />
               </div>
             </div>
             
             {isProjectDropdownOpen && (
               <div className="absolute z-50 w-full mt-2 bg-slate-800/95 backdrop-blur-xl border border-slate-700/50 rounded-xl shadow-[0_10px_40px_rgba(0,0,0,0.8)] overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200">
                 <div className="max-h-60 overflow-y-auto scrollbar-thin scrollbar-thumb-slate-600 scrollbar-track-transparent">
                   {uniqueProjects.filter(p => p.toLowerCase().includes(filterProjectName.toLowerCase())).length > 0 ? (
                     uniqueProjects.filter(p => p.toLowerCase().includes(filterProjectName.toLowerCase())).map(p => (
                       <div 
                         key={p} 
                         className="px-4 py-3 text-sm text-slate-300 hover:text-white hover:bg-indigo-500/20 cursor-pointer transition-colors border-b border-slate-700/30 last:border-0"
                         onClick={() => {
                           setFilterProjectName(p);
                           setIsProjectDropdownOpen(false);
                         }}
                       >
                         {p}
                       </div>
                     ))
                   ) : (
                     <div className="px-4 py-4 text-sm text-slate-500 italic text-center">No projects match your search</div>
                   )}
                 </div>
               </div>
             )}
          </div>
          <div className="flex-1 relative">
             <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2 block">Filter by Date</label>
             <div 
               className="relative cursor-pointer"
               onClick={() => setIsDateDropdownOpen(!isDateDropdownOpen)}
               tabIndex={0}
               onBlur={() => setTimeout(() => setIsDateDropdownOpen(false), 200)}
             >
               <div className="w-full bg-black/40 border border-slate-700 text-white rounded-xl px-4 py-3 flex items-center justify-between hover:bg-black/60 transition-colors shadow-inner">
                 <span className={filterDate ? "text-white font-medium" : "text-slate-500"}>
                   {filterDate || "All Dates"}
                 </span>
                 <ChevronDown className={`w-4 h-4 text-slate-500 transition-transform duration-300 ${isDateDropdownOpen ? 'rotate-180' : ''}`} />
               </div>
             </div>

             {isDateDropdownOpen && (
               <div className="absolute z-50 w-full mt-2 bg-slate-800/95 backdrop-blur-xl border border-slate-700/50 rounded-xl shadow-[0_10px_40px_rgba(0,0,0,0.8)] overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200">
                 <div className="max-h-60 overflow-y-auto scrollbar-thin scrollbar-thumb-slate-600 scrollbar-track-transparent">
                   <div 
                     className={`px-4 py-3 text-sm cursor-pointer transition-colors border-b border-slate-700/30 ${!filterDate ? 'bg-indigo-500/20 text-white font-bold' : 'text-slate-300 hover:text-white hover:bg-slate-700/50'}`}
                     onClick={() => {
                       setFilterDate('');
                       setIsDateDropdownOpen(false);
                     }}
                   >
                     All Dates
                   </div>
                   {uniqueDates.map(d => (
                     <div 
                       key={d} 
                       className={`px-4 py-3 text-sm cursor-pointer transition-colors border-b border-slate-700/30 last:border-0 ${filterDate === d ? 'bg-indigo-500/20 text-white font-bold' : 'text-slate-300 hover:text-white hover:bg-slate-700/50'}`}
                       onClick={() => {
                         setFilterDate(d);
                         setIsDateDropdownOpen(false);
                       }}
                     >
                       {d}
                     </div>
                   ))}
                 </div>
               </div>
             )}
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <div className="w-12 h-12 rounded-full border-4 border-slate-700 border-t-emerald-500 animate-spin"></div>
          </div>
        ) : Object.keys(groupedReports).length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 bg-slate-900/30 rounded-2xl border border-white/[0.02]">
            <div className="w-20 h-20 rounded-full bg-slate-800 flex items-center justify-center mb-4 border border-slate-700">
              <AlertTriangle className="w-10 h-10 text-slate-500" />
            </div>
            <p className="text-slate-400 text-lg">No archival reports found.</p>
          </div>
        ) : (
          <div className="flex gap-4 min-h-[500px] h-[calc(100vh-220px)] w-full">
            <style>{`.custom-scrollbar::-webkit-scrollbar { height: 8px; width: 6px; } .custom-scrollbar::-webkit-scrollbar-track { background: transparent; } .custom-scrollbar::-webkit-scrollbar-thumb { background: #334155; border-radius: 10px; } .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: #475569; }`}</style>
            
            {/* PANE 1: Dates */}
            <div className="w-[300px] flex flex-col bg-slate-900/50 border border-slate-700/50 rounded-2xl overflow-hidden shadow-inner shrink-0 h-fit max-h-full">
              <div className="p-4 bg-slate-800/80 border-b border-slate-700">
                <h3 className="text-white font-bold text-sm uppercase tracking-wider">Archival Dates</h3>
                <p className="text-xs text-slate-400 mt-1">{Object.keys(groupedReports).length} dates</p>
              </div>
              <div className="flex-1 overflow-y-auto p-3 custom-scrollbar space-y-2">
                {Object.keys(groupedReports).sort((a,b) => b.localeCompare(a)).map(date => {
                  const isSelected = selectedDate === date;
                  const count = groupedReports[date].length;
                  return (
                    <div 
                      key={date} 
                      onClick={() => { setSelectedDate(date); setSelectedReportId(null); }} 
                      className={`cursor-pointer p-3 rounded-xl border transition-all select-none group flex flex-col gap-2 shadow-sm ${isSelected ? 'bg-indigo-500/10 border-indigo-500 shadow-indigo-500/20' : 'bg-slate-800 border-slate-700 hover:border-slate-500 hover:bg-slate-700/80'}`}
                    >
                      <div className="flex items-center gap-2 overflow-hidden">
                        <div className="w-8 h-8 shrink-0 rounded-lg bg-indigo-500/20 flex items-center justify-center border border-indigo-500/30 text-indigo-400 font-bold text-xs">
                          <Calendar className="w-4 h-4" />
                        </div>
                        <div>
                          <h4 className={`font-bold text-sm truncate ${isSelected ? 'text-indigo-300' : 'text-slate-200'}`}>{date}</h4>
                          <p className="text-xs text-slate-400">{count} project{count !== 1 ? 's' : ''}</p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* PANE 2: Projects */}
            {(() => {
              if (!selectedDate) return null;
              const activeProjects = groupedReports[selectedDate];
              return (
                <div className="w-[320px] flex flex-col bg-slate-900/50 border border-slate-700/50 rounded-2xl overflow-hidden shadow-inner shrink-0 h-fit max-h-full">
                  <div className="p-4 bg-slate-800/80 border-b border-slate-700">
                    <h3 className="text-white font-bold text-sm uppercase tracking-wider">Projects on {selectedDate}</h3>
                    <p className="text-xs text-slate-400 mt-1">{activeProjects.length} items</p>
                  </div>
                  <div className="flex-1 overflow-y-auto p-3 custom-scrollbar space-y-2">
                    {activeProjects.map((report) => {
                      const isSelected = selectedReportId === report.id;
                      return (
                        <div 
                          key={report.id} 
                          onClick={() => setSelectedReportId(report.id)} 
                          className={`cursor-pointer p-3 rounded-xl border transition-all select-none group flex flex-col gap-2 shadow-sm ${isSelected ? 'bg-fuchsia-500/10 border-fuchsia-500 shadow-fuchsia-500/20' : 'bg-slate-800 border-slate-700 hover:border-slate-500 hover:bg-slate-700/80'}`}
                        >
                          <div className="flex items-center gap-2 justify-between">
                            <h4 className={`font-bold text-sm line-clamp-2 ${isSelected ? 'text-fuchsia-300' : 'text-slate-200'}`}>{report.report_name}</h4>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })()}

            {/* PANE 3: Project Details */}
            {(() => {
              if (!selectedDate || !selectedReportId) {
                return (
                  <div className="flex-1 flex flex-col items-center justify-center bg-slate-900/50 border border-slate-700/50 rounded-2xl h-fit max-h-full min-h-[300px]">
                    <svg className="w-16 h-16 text-slate-700 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                    <p className="text-slate-400 text-sm">Select a date and project to view details</p>
                  </div>
                );
              }

              const activeReport = groupedReports[selectedDate].find(r => r.id === selectedReportId);
              if (!activeReport) return null;

              return (
                <div className="flex-1 flex flex-col bg-slate-900/50 border border-slate-700/50 rounded-2xl overflow-hidden shadow-inner h-fit max-h-full">
                  <div className="p-4 bg-slate-800/80 border-b border-slate-700 flex justify-between items-center">
                    <div>
                      <h3 className="text-white font-bold text-sm uppercase tracking-wider">Project Details</h3>
                    </div>
                    <span className="text-xs font-mono bg-rose-500/10 text-rose-400 px-2 py-1 rounded border border-rose-500/20 whitespace-nowrap">
                      DELETED
                    </span>
                  </div>
                  
                  <div className="p-6">
                    <h2 className="text-2xl font-bold text-white mb-2">{activeReport.report_name}</h2>
                    <p className="text-sm text-slate-400 font-mono mb-8">Archived on: {activeReport.report_date || 'N/A'}</p>

                    <div className="flex flex-col sm:flex-row gap-4 mt-8">
                      <button
                        onClick={() => handleRestore(activeReport.id)}
                        className="flex-1 flex items-center justify-center gap-2 bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-400 border border-emerald-500/20 rounded-xl py-3 px-4 font-semibold transition-colors"
                      >
                        <RotateCcw className="w-5 h-5" /> Restore Project
                      </button>
                      <button
                        onClick={() => setConfirmModal({ isOpen: true, type: 'delete', report: activeReport, password: '', adminUsername: '', projectNameConfirm: '' })}
                        className="flex-1 flex items-center justify-center gap-2 bg-rose-500/10 hover:bg-rose-500/20 text-rose-400 border border-rose-500/20 rounded-xl py-3 px-4 font-semibold transition-colors"
                      >
                        <Trash2 className="w-5 h-5" /> Delete Permanently
                      </button>
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>
        )}

        {/* Permanent Delete Modal */}
        {confirmModal.isOpen && (
          <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-[100] p-4">
            <div className="bg-slate-900 border border-rose-500/30 rounded-3xl w-full max-w-md shadow-2xl overflow-hidden relative animate-in fade-in zoom-in duration-200">

              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-rose-500 to-orange-500"></div>

              <div className="p-6">
                <div className="w-12 h-12 bg-rose-500/20 rounded-2xl flex items-center justify-center mb-5 border border-rose-500/30">
                  <AlertTriangle className="w-6 h-6 text-rose-500" />
                </div>

                <h3 className="text-xl font-bold text-white mb-2">Permanent Delete</h3>
                <p className="text-slate-400 text-sm mb-6 leading-relaxed">
                  You are about to permanently delete <strong className="text-white">"{confirmModal.report?.report_name}"</strong>. This action cannot be undone. You must be an administrator to perform this action.
                </p>

                <form onSubmit={handlePermanentDelete} className="space-y-4">
                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider block">Confirm Project Name</label>
                      <button
                        type="button"
                        onClick={() => setConfirmModal({ ...confirmModal, projectNameConfirm: confirmModal.report?.report_name || '' })}
                        className="text-xs font-bold text-rose-500 hover:text-rose-400 bg-rose-500/10 hover:bg-rose-500/20 px-2 py-1 rounded transition flex items-center gap-1"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" /></svg>
                        Auto-fill
                      </button>
                    </div>
                    <div className="relative group mb-4">
                      <input
                        type="text"
                        required
                        value={confirmModal.projectNameConfirm}
                        onChange={(e) => setConfirmModal({ ...confirmModal, projectNameConfirm: e.target.value })}
                        placeholder={`Type "${confirmModal.report?.report_name}"`}
                        className="w-full bg-black/40 border border-slate-700 text-white placeholder-slate-500 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-rose-500/50 focus:border-rose-500 transition-all"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2 block">Admin Username</label>
                    <div className="relative group mb-4">
                      <span className="absolute inset-y-0 left-0 pl-4 flex items-center text-slate-500 group-focus-within:text-rose-400 transition-colors">
                        <Lock className="w-5 h-5" />
                      </span>
                      <input
                        type="text"
                        required
                        autoFocus
                        value={confirmModal.adminUsername}
                        onChange={(e) => setConfirmModal({ ...confirmModal, adminUsername: e.target.value })}
                        placeholder="Enter admin username"
                        className="w-full bg-black/40 border border-slate-700 text-white placeholder-slate-500 rounded-xl pl-11 pr-4 py-3 focus:outline-none focus:ring-2 focus:ring-rose-500/50 focus:border-rose-500 transition-all"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2 block">Admin Password</label>
                    <div className="relative group">
                      <span className="absolute inset-y-0 left-0 pl-4 flex items-center text-slate-500 group-focus-within:text-rose-400 transition-colors">
                        <Lock className="w-5 h-5" />
                      </span>
                      <input
                        type="password"
                        required
                        value={confirmModal.password}
                        onChange={(e) => setConfirmModal({ ...confirmModal, password: e.target.value })}
                        placeholder="Enter your password to confirm"
                        className="w-full bg-black/40 border border-slate-700 text-white placeholder-slate-500 rounded-xl pl-11 pr-4 py-3 focus:outline-none focus:ring-2 focus:ring-rose-500/50 focus:border-rose-500 transition-all"
                      />
                    </div>
                  </div>

                  <div className="flex gap-3 pt-4">
                    <button
                      type="button"
                      onClick={closeModal}
                      className="flex-1 bg-slate-800 hover:bg-slate-700 text-white py-3 rounded-xl font-semibold transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="flex-1 bg-gradient-to-r from-rose-600 to-red-500 hover:from-rose-500 hover:to-red-400 text-white py-3 rounded-xl font-bold shadow-[0_0_20px_rgba(225,29,72,0.3)] transition-all flex items-center justify-center gap-2"
                    >
                      <Trash2 className="w-4 h-4" /> Confirm Delete
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default BackupReports;
